/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ToastProps } from "./Toast";

/**
 * Props for the ToastProvider component
 * @extends PropsWithChildren
 */
export type ToastProviderProps = PropsWithChildren<{
  /**
   * Whether this toaster is used within a dialog context
   * @default false
   */
  isDialogToaster?: boolean;
}>;

/**
 * Context properties for the ToastProvider
 */
export type ToastProviderContextProps = {
  /**
   * Array of active toast notifications
   */
  toasts: ToastProps[];
  /**
   * Function to update the toast array
   */
  setToasts: Dispatch<SetStateAction<ToastProps[]>>;
  /**
   * Array of toast height measurements
   */
  toastHeights: { id: string; height: number }[];
  /**
   * Function to update toast heights
   */
  setToastHeights: Dispatch<
    SetStateAction<ToastProviderContextProps["toastHeights"]>
  >;
  /**
   * Function to calculate the total height of the toast stack
   */
  getToastStackHeight: (index?: number) => number;
  /**
   * Number of toasts to display before stacking
   */
  toastsBeforeStack: number;
  /**
   * Function to update the number of toasts before stacking
   */
  setToastsBeforeStack: Dispatch<SetStateAction<number>>;
  /**
   * Whether extra toasts are currently visible
   */
  extraToastsVisible: boolean;
  /**
   * Function to update the visibility of extra toasts
   */
  setExtraToastsVisible: Dispatch<SetStateAction<boolean>>;
  /**
   * Whether this toaster is used within a dialog context
   */
  isDialogToaster: boolean;
};

/**
 * Context for managing toast state and operations
 */
export const ToastProviderContext = createContext<ToastProviderContextProps>({
  toasts: [],
  setToasts: () => [],
  toastHeights: [],
  setToastHeights: () => [],
  getToastStackHeight: () => 0,
  toastsBeforeStack: 3,
  setToastsBeforeStack: () => [],
  extraToastsVisible: false,
  setExtraToastsVisible: () => [],
  isDialogToaster: false,
});

/**
 * Global reference to toast state setters for external access
 */
export const getSetToasts: {
  setToasts: ToastProviderContextProps["setToasts"];
  setToastHeights: ToastProviderContextProps["setToastHeights"];
} = {
  setToasts: () => [],
  setToastHeights: () => [],
};

/**
 * ToastProvider component for managing toast state and context.
 *
 * Features:
 * - Manages toast state and lifecycle
 * - Handles toast height tracking for positioning
 * - Provides stacking behavior configuration
 * - Supports dialog toaster mode
 * - Automatic duplicate toast detection and removal
 * - Context-based state management
 * - Height calculation utilities for positioning
 *
 * @example
 * <ToastProvider isDialogToaster={false}>
 *   <Toaster id="main-toaster" />
 * </ToastProvider>
 */
export const ToastProvider: FC<ToastProviderProps> = ({
  children,
  isDialogToaster: isDialogToasterProp = false,
}) => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const [toastHeights, setToastHeights] = useState<
    ToastProviderContextProps["toastHeights"]
  >([]);
  const [toastsBeforeStack, setToastsBeforeStack] = useState<number>(3);
  const [extraToastsVisible, setExtraToastsVisible] = useState<boolean>(false);
  getSetToasts.setToasts = setToasts;
  getSetToasts.setToastHeights = setToastHeights;
  const [isDialogToaster, setIsDialogToaster] =
    useState<boolean>(isDialogToasterProp);

  const removeDuplicateToasts = (toasts: ToastProps[]) => {
    const toastIds = new Set<ToastProps["id"]>();
    const uniqueToasts = toasts.filter((toast) => {
      if (toastIds.has(toast.id)) {
        console.error(
          `Encountered toast with duplicate id ${toast.id}. Each toast id needs to be unique.`,
        );
        return false;
      } else {
        toastIds.add(toast.id);
        return true;
      }
    });
    return uniqueToasts;
  };

  useEffect(() => {
    const uniqueToasts = removeDuplicateToasts(toasts);
    if (toasts.length !== uniqueToasts.length) {
      setToasts(uniqueToasts);
    }
  }, [toasts]);

  useEffect(() => {
    setIsDialogToaster(isDialogToasterProp);
  }, [isDialogToasterProp]);

  // Memoize the ID→height map once per toastHeights change, so
  // getToastStackHeight (called once per toast) doesn't rebuild it each time.
  const heightMap = useMemo(
    () => new Map(toastHeights.map((t) => [t.id, t.height])),
    [toastHeights],
  );

  const getToastStackHeight = useCallback(
    (index?: number) => {
      // Use the toasts array for ordering, filter to toasts after the given index
      const relevantToasts =
        index !== undefined ? toasts.slice(index + 1) : toasts;

      // Get heights for relevant toasts (only those that have reported heights)
      const heights = relevantToasts
        .map((t) => heightMap.get(t.id))
        .filter((h): h is number => h !== undefined);

      if (heights.length > 0) {
        if (extraToastsVisible) {
          return heights.reduce((total, current) => total + current);
        }
        return heights
          .slice(-toastsBeforeStack)
          .reduce((total, current) => total + current);
      }
      return 0;
    },
    [toasts, heightMap, extraToastsVisible, toastsBeforeStack],
  );

  const value = useMemo(
    () => ({
      toasts,
      setToasts,
      toastHeights,
      setToastHeights,
      getToastStackHeight,
      toastsBeforeStack,
      setToastsBeforeStack,
      extraToastsVisible,
      setExtraToastsVisible,
      isDialogToaster,
    }),
    [
      extraToastsVisible,
      getToastStackHeight,
      toastHeights,
      toasts,
      toastsBeforeStack,
      isDialogToaster,
    ],
  );

  return (
    <ToastProviderContext.Provider value={value}>
      {children}
    </ToastProviderContext.Provider>
  );
};
